/**
 * Detects a stale-build chunk-load failure and gates auto-reload to at
 * most one attempt per distinct client build SHA — not a time window, so a
 * genuinely new deploy landing seconds after a failed attempt is never
 * blocked, and a retry storm against the same still-broken build is never
 * allowed through twice.
 */

const STORAGE_KEY = "boon:chunk-reload-attempted-sha";

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk/.test(error.message) ||
    /Failed to fetch dynamically imported module/.test(error.message)
  );
}

function writeAttemptedSha(sha: string): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, sha);
  } catch {
    // Shouldn't happen if the read in shouldAttemptAutoReload already
    // succeeded, but stay defensive.
  }
}

/**
 * Call with the SHA the client is CURRENTLY running (`window.BOON_BUILD_SHA`)
 * — not a freshly-fetched server value — at the moment a chunk failure (or a
 * proactive mismatch) is detected. Returns true if the caller should proceed
 * with `window.location.reload()`.
 *
 * Callers fall back to the literal string `"unknown"` when `BOON_BUILD_SHA`
 * is unset (unauthenticated pages, or a dev environment before the layout
 * sets the global). That's an intentional catchall, not an oversight: the
 * first `"unknown"`-keyed call consumes the one-shot guard, so a second
 * chunk failure on a different page in the same tab session is blocked even
 * if unrelated to the first. Accepted trade-off — a rare double-failure
 * showing the manual-recovery card instead of auto-reloading is safer than
 * looping when there's no real SHA to key the guard on.
 */
export function shouldAttemptAutoReload(currentBuildSha: string): boolean {
  let attemptedSha: string | null;
  try {
    attemptedSha = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // sessionStorage unavailable (private mode / sandboxed iframe): the
    // guard can't survive an actual reload either way (a fresh reload
    // wipes any in-memory tracking too), so there is no way to guarantee a
    // reload won't loop if the underlying failure is persistent. Refuse to
    // auto-reload at all — the caller's manual-recovery fallback takes
    // over instead.
    return false;
  }

  if (attemptedSha === currentBuildSha) return false;
  writeAttemptedSha(currentBuildSha);
  return true;
}

/** Test-only: reset the sessionStorage guard. */
export function __resetChunkLoadRecoveryForTests(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to reset if storage is unavailable.
  }
}
